import { cn } from '@/lib/utils';
import { forwardRef, type SVGProps } from 'react';
import { LUCIDE_ICONS } from './lucide-icons';

/** The line width on Lucide's 24 grid, and the heavier line of a solid icon
 * (Lucide has no filled icons). */
const STROKE = 1.75;
const SOLID_STROKE = 2.5;

/** Solid icons whose Lucide shape is closed, so they fill cleanly instead:
 * the favourite star has to read apart from the rest. */
const FILLED = new Set(['star-solid']);

export interface PlainIconProps extends Omit<
  SVGProps<SVGSVGElement>,
  'ref' | 'fill' | 'stroke' | 'children'
> {
  /** The icon name (@/lib/icons): its Lucide icon and `data-icon`. */
  name: string;
  /** The bold look, for an active, selected or filled state. */
  solid?: boolean;
  title?: string;
}

/** An icon in the Plain style: its Lucide icon, in the text colour. */
export const PlainIcon = forwardRef<SVGSVGElement, PlainIconProps>(
  function PlainIcon({ name, solid, title, className, ...rest }, ref) {
    const Icon = LUCIDE_ICONS[name];
    if (!Icon) return null;
    const filled = solid && FILLED.has(name);
    return (
      <Icon
        ref={ref}
        focusable='false'
        {...rest}
        strokeWidth={solid && !filled ? SOLID_STROKE : STROKE}
        fill={filled ? 'currentColor' : 'none'}
        className={cn('cl-icon-plain', className)}
        data-icon={name}
        data-style='plain'
      >
        {title && <title>{title}</title>}
      </Icon>
    );
  }
);
